import React, {useCallback, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    IconButton,
    Menu,
    MenuItem,
    Stack,
    styled,
    Switch,
    Tab,
    Tabs,
    TextField,
    Typography
} from '@mui/material';
import {useTheme} from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PublishIcon from '@mui/icons-material/Publish';
import ViewListIcon from '@mui/icons-material/ViewList';
import PaletteIcon from '@mui/icons-material/Palette';
import SettingsIcon from '@mui/icons-material/Settings';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DownloadIcon from '@mui/icons-material/Download';
import Divider from '@mui/material/Divider';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import DesktopWindowsIcon from '@mui/icons-material/DesktopWindows';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import QRCodeSVG from 'react-qr-code';
// import MultipageQuestionsPanel from './multipage/sidebar/MultipageQuestionsPanel';
// import DesignPanel from './multipage/sidebar/DesignPanel';
// import SettingsPanel from './multipage/sidebar/SettingsPanel';
// import ScreenEditor from './multipage/ScreenEditor';
// import PreviewComponent from './preview/FormPreview';
import SimpleFormEditor from './simple/SimpleFormEditor';
import SimpleQuestionsPanel from './simple/SimpleQuestionsPanel';
import {useNavigate, useParams, useSearchParams} from "react-router-dom";
import {useMutation} from "react-query";
// import QuestionSettingsPanel from "./multipage/QuestionSettingsPanel";
import {v4 as uuidv4} from 'uuid';
import {
    AnyQuestion,
    DropdownQuestion,
    Form,
    FormPresentationType,
    LayoutType,
    MultipleChoiceQuestion,
    PictureChoiceQuestion,
    QuestionType
} from "crqlar-react-common/dist/models/forms/form";
import {useUnsavedChangesWarning} from "crqlar-react-common/dist/utils/useUnsavedChangesWarning";
import {FormService} from "crqlar-react-common/dist/services/forms/form.service";
import {useSnackbar} from "notistack";
import UnsavedChangesDialog from "crqlar-react-common/dist/components/UnsavedChangesDialog";
import {useEntity} from "crqlar-react-common/dist/components/useEntity";
import FormPreview from "./preview/FormPreview";
import DesignPanel from "./DesignPanel";
import SettingsPanel from "./SettingsPanel";
import MultipageQuestionsPanel from "./multipage/MultipageQuestionsPanel";
import ScreenEditor from "./multipage/ScreenEditor";
import QuestionSettingsPanel from "./multipage/QuestionSettingsPanel";
import {useReadOnly} from "crqlar-react-common/dist/utils/hooks/users/useReadOnly";
import SettingsPageHeader from "../../system/layout/SettingsPageHeader";

const generateId = () => Math.random().toString(36).substring(2, 15);

// Styled components
const SidebarContainer = styled(Box)(({theme}) => ({
    display: 'flex',
    flexDirection: 'column',
    width: 320,
    borderRight: `1px solid ${theme.palette.divider}`,
    height: '100%',
    backgroundColor: theme.palette.background.paper,
    overflow: 'hidden',
    '& > div:last-child': {
        flex: 1,
        overflowY: 'auto',
        overflowX: 'hidden'
    }
}));

const EditorContainer = styled(Box)(({theme}) => ({
    flex: 1,
    height: '100%',
    backgroundColor: theme.palette.grey[100],
    overflow: 'auto'
}));

const headerButtonSx = {
    height: '40px',
    minHeight: '40px',
};

// Enum for sidebar tabs
enum SidebarTab {
    SCREENS = 0,
    DESIGN = 1,
    SETTINGS = 2,
}

const FormDetails: React.FC = () => {

    //region State

    const {id} = useParams();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const {enqueueSnackbar} = useSnackbar()
    const {t} = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    // Read-only users may open a form and browse it, but cannot create/update/delete anything:
    // no publish, no duplicate, no title edit, and the editor + sidebar panels are made inert.
    const readOnly = useReadOnly();
    // Marks a subtree non-interactive for read-only users (covers buttons, drag handles, text
    // fields and MUI Selects). Applied to a non-scrolling wrapper so scrolling still works.
    const applyInert = useCallback((el: HTMLElement | null) => {
        if (el) el.inert = readOnly;
    }, [readOnly]);

    // Get initial form values from query params if creating new form
    const formNameParam = searchParams.get('name');
    const formTypeParam = searchParams.get('type') as FormPresentationType | null;
    const previewParam = searchParams.get('preview');
    const previewModeParam = searchParams.get('mode') || 'standalone';

    const newForm = {
        id: generateId(),
        title: formNameParam || t('New Form'),
        description: '',
        version: 1,
        presentationType: formTypeParam || FormPresentationType.MULTI_PAGE,
        questions: [{
            id: generateId(),
            type: QuestionType.WELCOME,
            title: t('Welcome to our form'),
            description: t('Please answer the following questions to help us understand your needs.'),
            layout: LayoutType.DEFAULT
        }],
        settings: {
            showProgressBar: true
        },
        theme: {
            backgroundColor: '#1976d2',
            primaryColor: "#1976d2"
        }
    } as Form
    const [currentSidebarTab, setCurrentSidebarTab] = useState<SidebarTab>(SidebarTab.SCREENS);
    const [currentQuestionId, setCurrentQuestionId] = useState<string>(newForm?.questions?.[0]?.id || '');
    const [showShareDialog, setShowShareDialog] = useState(false);
    const [publicFormUrl, setPublicFormUrl] = useState<string>('');
    const [previewMenuAnchorEl, setPreviewMenuAnchorEl] = useState<null | HTMLElement>(null);
    const [moreActionsAnchorEl, setMoreActionsAnchorEl] = useState<null | HTMLElement>(null);
    const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
    const [duplicateFormName, setDuplicateFormName] = useState('');
    const [openInNewTab, setOpenInNewTab] = useState(false);

    // Sync preview state with URL search params
    const showPreview = previewParam === 'true';
    const previewMode = previewModeParam as 'standalone' | 'app' | 'embed';

    const formData = useEntity({
        get: async () => {
            return FormService.getFormById(id!!)
        },
        create: FormService.createForm,
        update: FormService.updateForm,
        delete: (id) => FormService.deleteForm({id: [id]}),
        onDeleted: () => navigate(-1),
        isValid: (form) => {
            return !!form.title
        },
        cleanupBeforeSave: (form) => {
            return {
                ...form,
                name: form.title?.trim()
            }
        },
        onSaved: form => {
            // Set the public URL and show the share dialog
            const id = form?.id;
            if (id) {
                const url = `${window.location.origin}/public/form/${id}`;
                setPublicFormUrl(url);
                setShowShareDialog(true);
            }
        },
        onFetched: item => {
            setCurrentQuestionId(item.questions?.[0]?.id || '')
        }
    }, id)

    // Use the unsaved changes warning hook
    const {showDialog, handleConfirmNavigation, handleCancelNavigation} = useUnsavedChangesWarning({
        hasUnsavedChanges: formData.isDirty
    });

    const form = formData.item
    const setForm = formData.setItem

    const duplicateFormMut = useMutation({
        mutationFn: async (formData: Form) => {
            return FormService.createForm(formData);
        },
        onSuccess: (response) => {
            enqueueSnackbar(t('Form duplicated successfully'), {variant: 'success'});

            // Navigate to the newly created form
            if (response?.id) {
                if (openInNewTab) {
                    window.open(`/system/forms/${response.id}`, '_blank');
                } else {
                    navigate(`/system/forms/${response.id}`);
                }
            }

            // Reset dialog state
            setShowDuplicateDialog(false);
            setDuplicateFormName('');
            setOpenInNewTab(false);
        },
        onError: (error) => {
            enqueueSnackbar((error as Error).message || t('Failed to duplicate form'), {variant: 'error'});
        }
    });

    // Get current question
    const currentQuestion = form?.questions?.find(question => question.id === currentQuestionId);

    // Check if this is a simple (single-page) form
    const isSimpleForm = form?.presentationType === FormPresentationType.SINGLE_PAGE;

    // Handlers
    const handleSidebarTabChange = (_event: React.SyntheticEvent, newValue: SidebarTab) => {
        setCurrentSidebarTab(newValue);
    };

    const handleQuestionSelect = (questionId: string) => {
        setCurrentQuestionId(questionId);
    };

    const handleQuestionUpdate = async (updatedQuestion: AnyQuestion) => {
        if (!form || !form.questions) return;

        try {
            const questionIndex = form.questions.findIndex(question => question.id === updatedQuestion.id);

            if (questionIndex === -1) {
                return null;
            }

            const updatedQuestions = [...form.questions];
            updatedQuestions[questionIndex] = updatedQuestion;

            const updatedForm = {
                ...form,
                questions: updatedQuestions
            }

            setForm(updatedForm);
        } catch (error) {
            console.error('Error updating question:', error);
        }
    };

    const handleQuestionAdd = async (questionType: QuestionType) => {
        if (!form) return;

        try {
            const newQuestion: AnyQuestion = {
                id: generateId(),
                type: questionType,
                title: getDefaultTitleForType(questionType),
                layout: LayoutType.DEFAULT,
            } as AnyQuestion;

            const updatedQuestions = [...(form.questions || []), newQuestion];
            const updatedForm = {
                ...form,
                questions: updatedQuestions
            }

            setForm(updatedForm);
            // Set the new question as the current question
            setCurrentQuestionId(newQuestion.id);
        } catch (error) {
            console.error('Error adding question:', error);
        }
    };

    const handleQuestionDelete = async (questionId: string) => {
        if (!form || !form.questions) return;

        try {
            const questionIndex = form.questions.findIndex(question => question.id === questionId);
            if (questionIndex === -1) {
                return null;
            }

            const updatedQuestions = [...form.questions];
            updatedQuestions.splice(questionIndex, 1);

            const updatedForm = {
                ...form,
                questions: updatedQuestions
            }

            setForm(updatedForm);

            // If the current question was deleted, select the first question
            if (questionId === currentQuestionId && updatedForm.questions && updatedForm.questions.length > 0) {
                setCurrentQuestionId(updatedForm.questions[0].id);
            }
        } catch (error) {
            console.error('Error deleting question:', error);
        }
    };

    const handleQuestionDuplicate = async (questionId: string) => {
        if (!form || !form.questions) return;

        try {
            const questionToDuplicate = form.questions.find(question => question.id === questionId);

            if (!questionToDuplicate) {
                return null;
            }

            // Create a deep copy with new ID
            const duplicatedQuestion: AnyQuestion = JSON.parse(JSON.stringify(questionToDuplicate));
            duplicatedQuestion.id = generateId();

            const updatedForm: Form = {
                ...form,
                questions: [...form.questions, duplicatedQuestion]
            };

            setForm(updatedForm);

            // Set the duplicated question as the current question
            setCurrentQuestionId(duplicatedQuestion.id);
        } catch (error) {
            console.error('Error duplicating question:', error);
        }
    };

    const handleQuestionsReorder = async (questionIds: string[]) => {
        if (!form || !form.questions) return;

        try {
            // Map of questions by ID for quick lookup
            const questionsMap = new Map(
                form.questions.map(question => [question.id, question])
            );

            // Create new ordered array
            const reorderedQuestions = questionIds
                .filter(id => questionsMap.has(id))
                .map(id => questionsMap.get(id)!);

            // Add any questions that weren't in the provided order
            form.questions.forEach(question => {
                if (!questionIds.includes(question.id)) {
                    reorderedQuestions.push(question);
                }
            });

            setForm({
                ...form,
                questions: reorderedQuestions,
            })
        } catch (error) {
            console.error('Error reordering questions:', error);
        }
    };

    const handleFormUpdate = async (updatedForm: Partial<Form>) => {
        if (!form) return;

        try {
            setForm({
                ...form,
                ...updatedForm
            })
        } catch (error) {
            console.error('Error updating form:', error);
        }
    };

    const handlePreviewMenuClick = (event: React.MouseEvent<HTMLElement>) => {
        setPreviewMenuAnchorEl(event.currentTarget);
    };

    const handlePreviewMenuClose = () => {
        setPreviewMenuAnchorEl(null);
    };

    const handlePreviewToggle = (mode: 'standalone' | 'app' | 'embed') => {
        handlePreviewMenuClose();

        if (showPreview) {
            // Remove preview param to close preview (enables browser back button)
            const params = new URLSearchParams(searchParams);
            params.delete('preview');
            params.delete('mode');
            setSearchParams(params);
        } else {
            // Add preview param to open preview with specified mode
            const params = new URLSearchParams(searchParams);
            params.set('preview', 'true');
            params.set('mode', mode);
            setSearchParams(params);
        }
    };

    const handleCopyFormUrl = () => {
        navigator.clipboard.writeText(publicFormUrl);
        enqueueSnackbar(t('Form link copied to clipboard'), {variant: 'info'});
    };

    const handleOpenFormUrl = () => {
        window.open(publicFormUrl, '_blank');
    };

    const handleDownloadQR = () => {
        if (!publicFormUrl) return;

        const svg = document.getElementById('share-dialog-qr-code');
        if (!svg) return;

        const svgData = new XMLSerializer().serializeToString(svg);
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const img = new Image();

        img.onload = () => {
            canvas.width = img.width;
            canvas.height = img.height;
            ctx?.drawImage(img, 0, 0);

            canvas.toBlob((blob) => {
                if (blob) {
                    const url = URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = `${form?.title || 'form'}-qr-code.png`;
                    link.click();
                    URL.revokeObjectURL(url);
                }
            });
        };

        img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
    };

    const handleCloseShareDialog = () => {
        setShowShareDialog(false);
        // Navigate to the form after closing the dialog
        const id = form?.id;
        if (id) {
            navigate(`/system/forms/${id}`, {replace: true});
        }
    };

    const handleMoreActionsClick = (event: React.MouseEvent<HTMLElement>) => {
        setMoreActionsAnchorEl(event.currentTarget);
    };

    const handleMoreActionsClose = () => {
        setMoreActionsAnchorEl(null);
    };

    const handleDuplicateClick = () => {
        handleMoreActionsClose();
        if (form) {
            setDuplicateFormName(`${form.title} Copy`);
            setShowDuplicateDialog(true);
        }
    };

    const handleDuplicateDialogClose = () => {
        setShowDuplicateDialog(false);
        setDuplicateFormName('');
        setOpenInNewTab(false);
    };

    const cloneFormWithNewIds = (originalForm: Form): Form => {
        const clonedForm: Form = {
            ...originalForm,
            id: uuidv4(),
            questions: originalForm.questions?.map((question) => {
                const clonedQuestion: AnyQuestion = {
                    ...question,
                    id: uuidv4()
                };

                // Regenerate option IDs for questions that have options
                if ('options' in clonedQuestion && clonedQuestion.options) {
                    const questionWithOptions = clonedQuestion as MultipleChoiceQuestion | PictureChoiceQuestion | DropdownQuestion;
                    questionWithOptions.options = questionWithOptions.options?.map(option => ({
                        ...option,
                        id: uuidv4()
                    }));
                }

                return clonedQuestion;
            })
        };

        return clonedForm;
    };

    const handleDuplicateConfirm = () => {
        if (!form || !duplicateFormName.trim()) return;

        // Clone the form and regenerate all IDs
        const clonedForm = cloneFormWithNewIds(form);
        clonedForm.title = duplicateFormName.trim();
        clonedForm.version = 1;

        // Create the duplicated form
        duplicateFormMut.mutate(clonedForm);
    };

    // Helper function to get a default title based on question type
    const getDefaultTitleForType = (type: QuestionType): string => {
        switch (type) {
            case QuestionType.WELCOME:
                return t('Welcome to our form');
            case QuestionType.MULTIPLE_CHOICE:
                return t('Select one of the following options');
            case QuestionType.YES_NO:
                return t('Do you agree with the following statement?');
            case QuestionType.SHORT_TEXT:
                return t('Please provide a short answer');
            case QuestionType.LONG_TEXT:
                return t('Please provide a detailed response');
            case QuestionType.RATING:
                return t('How would you rate this?');
            default:
                return t('New Question');
        }
    };

    const pageHeader = (
        <SettingsPageHeader
            title={form?.title}
            back={() => navigate('/system/forms')}
            loading={formData.loading || formData.saving}
            actions={!formData.loading && !formData.isMissing ? (
                <>
                    <Button
                        size="small"
                        startIcon={isMobile ? undefined : <PublishIcon/>}
                        onClick={() => formData.save()}
                        disabled={formData.loading || formData.saving || !formData.isValid || readOnly}
                        variant="contained"
                        sx={{minWidth: 0, px: 1.25}}
                    >
                        {formData.saving ? t('Publishing...') : t('Publish')}
                    </Button>
                    {!isMobile && (
                        <Button
                            startIcon={<PlayArrowIcon/>}
                            endIcon={<ArrowDropDownIcon/>}
                            size="small"
                            sx={headerButtonSx}
                            onClick={handlePreviewMenuClick}
                        >
                            {t('Preview')}
                        </Button>
                    )}
                    {(isMobile || id) && (
                        <IconButton
                            size="small"
                            aria-label={t('More Actions')}
                            onClick={handleMoreActionsClick}
                            sx={{width: 36, height: 36, borderRadius: 1}}
                        >
                            <MoreVertIcon fontSize="small"/>
                        </IconButton>
                    )}
                </>
            ) : undefined}
        />
    )

    // Show loading state
    if (formData.loading) {
        return (
            <>
                {pageHeader}
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: '100%',
                    height: {
                        xs: '100%',
                        md: 'calc(100dvh - var(--banner-offset, 0px) - 96px)'
                    }
                }}>
                    <CircularProgress/>
                </Box>
            </>
        );
    }

    if (formData.isMissing) {
        return (
            <>
                {pageHeader}
                <Box sx={{display: 'flex', justifyContent: 'center', pt: 6}}>
                    <Typography color="text.secondary">{t('Form not found')}</Typography>
                </Box>
            </>
        );
    }

    return (
        <>
            {pageHeader}
            <Stack
            direction={'column'}
            alignItems={'flex-start'}
            sx={{
                width: {xs: 'calc(100% + 24px)', md: 'calc(100% + 48px)'},
                height: '100%',
                mx: {xs: -1.5, md: -3},
                overflow: 'hidden',
            }}
        >
            {/* Preview Menu */}
            <Menu
                anchorEl={previewMenuAnchorEl}
                open={Boolean(previewMenuAnchorEl)}
                onClose={handlePreviewMenuClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left'
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left'
                }}
            >
                <MenuItem onClick={() => handlePreviewToggle('standalone')}>
                    <DesktopWindowsIcon fontSize="small" sx={{mr: 1}}/>
                    {t('Standalone Preview')}
                </MenuItem>
                {/*<MenuItem onClick={() => handlePreviewToggle('app')}>*/}
                {/*    <SmartphoneIcon fontSize="small" sx={{mr: 1}}/>*/}
                {/*    App Preview*/}
                {/*</MenuItem>*/}
                {/*<MenuItem onClick={() => handlePreviewToggle('embed')}>*/}
                {/*    <CodeIcon fontSize="small" sx={{mr: 1}}/>*/}
                {/*    Embedded Preview*/}
                {/*</MenuItem>*/}
            </Menu>

            {/* More Actions Menu */}
            <Menu
                anchorEl={moreActionsAnchorEl}
                open={Boolean(moreActionsAnchorEl)}
                onClose={handleMoreActionsClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left'
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left'
                }}
            >
                {isMobile && (
                    <MenuItem
                        onClick={() => {
                            handleMoreActionsClose();
                            handlePreviewToggle('standalone');
                        }}
                    >
                        <DesktopWindowsIcon fontSize="small" sx={{mr: 1}}/>
                        {t('Preview')}
                    </MenuItem>
                )}
                <MenuItem onClick={handleDuplicateClick} disabled={readOnly}>
                    <ContentCopyIcon fontSize="small" sx={{mr: 1}}/>
                    {t('Duplicate')}
                </MenuItem>
            </Menu>

            {/* Main Content */}
            <Box sx={{
                display: 'flex',
                width: '100%',
                flex: 1,
                minHeight: 0,
                bgcolor: 'background.paper',
                overflow: 'hidden'
            }}>
                {isSimpleForm ? (
                    <>
                        {/* Simple Form - Sidebar */}
                        <SidebarContainer>
                            <Tabs
                                value={currentSidebarTab}
                                onChange={handleSidebarTabChange}
                                aria-label={t('Sidebar tabs')}
                                centered
                                sx={{
                                    borderBottom: 1,
                                    borderColor: 'divider',
                                    flexShrink: 0
                                }}
                            >
                                <Tab sx={{flexGrow: 1}} icon={<ViewListIcon/>} aria-label={t('Questions')}/>
                                <Tab sx={{flexGrow: 1}} icon={<PaletteIcon/>} aria-label={t('Design')}/>
                                <Tab sx={{flexGrow: 1}} icon={<SettingsIcon/>} aria-label={t('Settings')}/>
                            </Tabs>

                            <Box sx={{flex: 1, overflowY: 'auto', overflowX: 'hidden'}}>
                                {/* Read-only users: panels are inert (no add/delete/reorder/edit) */}
                                <Box ref={applyInert} sx={{opacity: readOnly ? 0.6 : undefined}}>
                                    {currentSidebarTab === SidebarTab.SCREENS &&
                                        <SimpleQuestionsPanel
                                            questions={form.questions || []}
                                            currentQuestionId={currentQuestionId}
                                            onQuestionSelect={handleQuestionSelect}
                                            onQuestionAdd={handleQuestionAdd}
                                            onQuestionDelete={handleQuestionDelete}
                                            onQuestionDuplicate={handleQuestionDuplicate}
                                            onQuestionsReorder={handleQuestionsReorder}
                                        />
                                    }

                                    {currentSidebarTab === SidebarTab.DESIGN && (
                                        <DesignPanel
                                            theme={form.theme || {}}
                                            onThemeUpdate={(theme) => handleFormUpdate({theme})}
                                            presentationType={FormPresentationType.SINGLE_PAGE}
                                        />
                                    )}

                                    {currentSidebarTab === SidebarTab.SETTINGS && (
                                        <SettingsPanel
                                            title={form.title || ''}
                                            onTitleChange={title => handleFormUpdate({title})}
                                            settings={form.settings || {}}
                                            onSettingsUpdate={(settings) => handleFormUpdate({settings})}
                                            sectorId={form.sectorId}
                                            onSectorChange={(sectorId) => handleFormUpdate({sectorId})}
                                            disabled={readOnly}
                                        />
                                    )}
                                </Box>
                            </Box>
                        </SidebarContainer>

                        {/* Simple Form - Editor Area */}
                        <EditorContainer>
                            {/* Read-only users: the inline question editor is inert (view only) */}
                            <Box ref={applyInert} sx={{height: '100%', opacity: readOnly ? 0.6 : undefined}}>
                                <SimpleFormEditor
                                    form={form}
                                    currentQuestionId={currentQuestionId}
                                    onQuestionSelect={handleQuestionSelect}
                                    onQuestionUpdate={handleQuestionUpdate}
                                />
                            </Box>
                        </EditorContainer>
                    </>
                ) : (
                    <>
                        {/* Multi-Page Form - Sidebar */}
                        <SidebarContainer>
                            <Tabs
                                value={currentSidebarTab}
                                onChange={handleSidebarTabChange}
                                aria-label={t('Sidebar tabs')}
                                centered
                                sx={{
                                    borderBottom: 1,
                                    borderColor: 'divider',
                                    flexShrink: 0
                                }}
                            >
                                <Tab sx={{flexGrow: 1}} icon={<ViewListIcon/>} aria-label={t('Screens')}/>
                                <Tab sx={{flexGrow: 1}} icon={<PaletteIcon/>} aria-label={t('Design')}/>
                                <Tab sx={{flexGrow: 1}} icon={<SettingsIcon/>} aria-label={t('Settings')}/>
                            </Tabs>

                            <Box sx={{flex: 1, overflowY: 'auto', overflowX: 'hidden'}}>
                                {currentSidebarTab === SidebarTab.SCREENS && (
                                    <MultipageQuestionsPanel
                                        questions={form.questions || []}
                                        currentQuestionId={currentQuestionId}
                                        onQuestionSelect={handleQuestionSelect}
                                        onQuestionAdd={handleQuestionAdd}
                                        onQuestionDelete={handleQuestionDelete}
                                        onQuestionDuplicate={handleQuestionDuplicate}
                                        onQuestionsReorder={handleQuestionsReorder}
                                    />
                                )}

                                {currentSidebarTab === SidebarTab.DESIGN && (
                                    <DesignPanel
                                        theme={form.theme || {}}
                                        onThemeUpdate={(theme) => handleFormUpdate({theme})}
                                        presentationType={FormPresentationType.MULTI_PAGE}
                                    />
                                )}

                                {currentSidebarTab === SidebarTab.SETTINGS && (
                                    <SettingsPanel
                                        title={form.title || ''}
                                        onTitleChange={title => handleFormUpdate({title})}
                                        settings={form.settings || {}}
                                        onSettingsUpdate={(settings) => handleFormUpdate({settings})}
                                        sectorId={form.sectorId}
                                        onSectorChange={(sectorId) => handleFormUpdate({sectorId})}
                                        disabled={readOnly}
                                    />
                                )}
                            </Box>
                        </SidebarContainer>

                        {/* Multi-Page Form - Editor Area */}
                        <EditorContainer>
                            {currentQuestion && (
                                <ScreenEditor
                                    question={currentQuestion}
                                    onQuestionUpdate={handleQuestionUpdate}
                                    form={form}
                                />
                            )}
                        </EditorContainer>

                        {/* Multi-Page Form - Settings Panel */}
                        {currentQuestion && (
                            <QuestionSettingsPanel
                                question={currentQuestion}
                                form={form}
                                onQuestionUpdate={handleQuestionUpdate}
                            />
                        )}
                    </>
                )}
            </Box>

            {/* Preview Dialog */}
            {showPreview && form && (
                <Dialog
                    fullScreen
                    open={showPreview}
                    onClose={() => {
                        const params = new URLSearchParams(searchParams);
                        params.delete('preview');
                        params.delete('mode');
                        setSearchParams(params);
                    }}
                >
                    <FormPreview
                        form={form}
                        previewMode={previewMode}
                        onClose={() => {
                            const params = new URLSearchParams(searchParams);
                            params.delete('preview');
                            params.delete('mode');
                            setSearchParams(params);
                        }}
                    />
                </Dialog>
            )}

            {/* Share Dialog */}
            <Dialog
                open={showShareDialog}
                onClose={handleCloseShareDialog}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>{t('Form Published Successfully!')}</DialogTitle>
                <DialogContent>
                    <Stack spacing={3} sx={{mt: 1}}>
                        <Typography variant="body1">
                            {t('Your form is now live and ready to share.')}
                        </Typography>

                        {/* Standalone Link Section */}
                        <Box>
                            <Typography variant="h6" gutterBottom sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                                <OpenInNewIcon fontSize="small"/>
                                {t('Standalone Link')}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{mb: 1, display: 'block'}}>
                                {t('Direct link to your form')}
                            </Typography>
                            <Box
                                sx={{
                                    p: 2,
                                    backgroundColor: 'grey.100',
                                    borderRadius: 1,
                                    wordBreak: 'break-all',
                                    mb: 1
                                }}
                            >
                                <Typography variant="body2" sx={{fontFamily: 'monospace'}}>
                                    {publicFormUrl}
                                </Typography>
                            </Box>
                            <Stack direction="row" spacing={1}>
                                <Button
                                    startIcon={<ContentCopyIcon/>}
                                    variant="outlined"
                                    size="small"
                                    onClick={handleCopyFormUrl}
                                >
                                    {t('Copy Link')}
                                </Button>
                                <Button
                                    startIcon={<OpenInNewIcon/>}
                                    variant="outlined"
                                    size="small"
                                    onClick={handleOpenFormUrl}
                                >
                                    {t('Open Link')}
                                </Button>
                            </Stack>
                        </Box>

                        <Divider/>

                        {/* QR Code Section */}
                        <Box>
                            <Typography variant="h6" gutterBottom>
                                {t('QR Code')}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{mb: 2, display: 'block'}}>
                                {t('Share your form with a scannable QR code')}
                            </Typography>

                            {/* QR Code Display */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    p: 3,
                                    backgroundColor: 'grey.50',
                                    borderRadius: 1,
                                    mb: 2
                                }}
                            >
                                <QRCodeSVG
                                    id="share-dialog-qr-code"
                                    value={publicFormUrl}
                                    size={200}
                                    level="H"
                                />
                            </Box>

                            {/* Download QR Button */}
                            <Box sx={{display: 'flex', justifyContent: 'center'}}>
                                <Button
                                    startIcon={<DownloadIcon />}
                                    variant="outlined"
                                    size="small"
                                    onClick={handleDownloadQR}
                                >
                                    {t('Download QR Code')}
                                </Button>
                            </Box>
                        </Box>

                        <Divider/>

                        {/*                        /!* Embed Code Section *!/*/}
                        {/*                        <Box>*/}
                        {/*                            <Typography variant="h6" gutterBottom sx={{display: 'flex', alignItems: 'center', gap: 1}}>*/}
                        {/*                                <CodeIcon fontSize="small"/>*/}
                        {/*                                Embed on Your Website*/}
                        {/*                            </Typography>*/}
                        {/*                            <Typography variant="caption" color="text.secondary" sx={{mb: 1, display: 'block'}}>*/}
                        {/*                                Add this code to any webpage*/}
                        {/*                            </Typography>*/}
                        {/*                            <Paper*/}
                        {/*                                sx={{*/}
                        {/*                                    p: 2,*/}
                        {/*                                    bgcolor: '#1e1e1e',*/}
                        {/*                                    color: '#d4d4d4',*/}
                        {/*                                    borderRadius: 1,*/}
                        {/*                                    mb: 1*/}
                        {/*                                }}*/}
                        {/*                            >*/}
                        {/*                                <pre style={{*/}
                        {/*                                    margin: 0,*/}
                        {/*                                    fontFamily: '"Courier New", monospace',*/}
                        {/*                                    fontSize: '0.75rem',*/}
                        {/*                                    lineHeight: 1.5,*/}
                        {/*                                    overflow: 'auto'*/}
                        {/*                                }}>*/}
                        {/*{`<div data-form-id="${form?.id}"></div>*/}
                        {/*<script src="${import.meta.env.VITE_APP_EMBED_URL || 'https://app.protocolcrm.com/embed'}/protocol-forms.js"></script>`}*/}
                        {/*                                </pre>*/}
                        {/*                            </Paper>*/}
                        {/*                            <Stack direction="row" spacing={1}>*/}
                        {/*                                <Button*/}
                        {/*                                    startIcon={<ContentCopyIcon/>}*/}
                        {/*                                    variant="outlined"*/}
                        {/*                                    size="small"*/}
                        {/*                                    onClick={handleCopyEmbedCode}*/}
                        {/*                                >*/}
                        {/*                                    Copy Embed Code*/}
                        {/*                                </Button>*/}
                        {/*                                <Button*/}
                        {/*                                    startIcon={<CodeIcon/>}*/}
                        {/*                                    variant="outlined"*/}
                        {/*                                    size="small"*/}
                        {/*                                    onClick={handleOpenEmbedPreview}*/}
                        {/*                                >*/}
                        {/*                                    Preview Embedded*/}
                        {/*                                </Button>*/}
                        {/*                            </Stack>*/}
                        {/*                        </Box>*/}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseShareDialog} variant="contained">
                        {t('Continue to Form Details')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Duplicate Form Dialog */}
            <Dialog
                open={showDuplicateDialog}
                onClose={duplicateFormMut.isLoading ? undefined : handleDuplicateDialogClose}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>{t('Duplicate Form')}</DialogTitle>
                <DialogContent>
                    <Stack spacing={3} sx={{mt: 2}}>
                        <TextField
                            autoFocus
                            fullWidth
                            label={t('Form Name')}
                            placeholder={t('Enter form name')}
                            value={duplicateFormName}
                            onChange={(e) => setDuplicateFormName(e.target.value)}
                            variant="outlined"
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={openInNewTab}
                                    onChange={(e) => setOpenInNewTab(e.target.checked)}
                                />
                            }
                            label={t('Open in new tab')}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions sx={{px: 3, pb: 3}}>
                    <Button onClick={handleDuplicateDialogClose} disabled={duplicateFormMut.isLoading}>
                        {t('Cancel')}
                    </Button>
                    <Button
                        onClick={handleDuplicateConfirm}
                        variant="contained"
                        disabled={!duplicateFormName.trim() || duplicateFormMut.isLoading}
                        startIcon={duplicateFormMut.isLoading ? <CircularProgress size={16}/> : null}
                    >
                        {duplicateFormMut.isLoading ? t('Duplicating...') : t('Duplicate')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Unsaved Changes Dialog */}
            <UnsavedChangesDialog
                open={showDialog}
                onConfirm={handleConfirmNavigation}
                onCancel={handleCancelNavigation}
            />

            {/*<DevelopmentWarning />*/}
            </Stack>
        </>
    );
};

export default FormDetails;
